import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import _ from 'lodash';
import GET_MDDAP_ORG_UNIT_PROGRAM_YEARS from '../queries/getMadapOrgUnitProgramYearsQuery';
import GET_ORG_UNITS_PERFORMANCE_REPORT from '../queries/getOrgUnitPerformanceReportQuery';
import SET_ORG_UNIT_SHARED_DATA from '../queries/setOrgUnitSharedDataMutation';
import GET_ORG_UNIT_SHARED_DATA from '../queries/getOrgUnitSharedDataQuery';
import ApolloError from '../../shared/components/ApolloError';
import SelectionList from '../../shared/components/SelectionList';
import ShareFutureDataCheckBox from './ShareFutureDataCheckBox';
import { TitleLabel } from './common/Labels';
import ActionButtonsGroup from '../../shared/components/ActionButtonsGroup';
import { getHasHavePlural } from '../../../utils/plurals';
import { joinFormatted } from '../../../utils/arrayExtensions';
import SuccessMessage from '../../shared/components/SuccessMessage';

/**
 * @typedef {object} FdaReportManagementProps
 * @property {*} [orgUnitId]
 */

/**
 * @param {FdaReportManagementProps} props
 */
const FdaReportManagement = props => {
  const [selectedPerformanceItems, setSelectedPerformanceItems] = useState({
    options: [],
    isShareFuture: false
  });
  const [selectedAppraisalProgramYears, setSelectedAppraisalProgramYears] = useState({
    options: [],
    isShareFuture: false
  });
  const [selectedPracticeProgramYears, setSelectedPracticeProgramYears] = useState({
    options: [],
    isShareFuture: false
  });
  const [isEdit, setIsEdit] = useState(false);
  const [resultMessage, setResultMessage] = useState(/** @type {React.ReactNode} */ (''));

  const {
    data: appraisalData,
    error: appraisalDataError,
    loading: appraisalDataLoading
  } = useQuery(GET_MDDAP_ORG_UNIT_PROGRAM_YEARS, {
    variables: { id: props.orgUnitId }
  });

  const {
    data: performanceData,
    error: performanceDataError,
    loading: performanceDataLoading
  } = useQuery(GET_ORG_UNITS_PERFORMANCE_REPORT, {
    variables: { id: props.orgUnitId }
  });

  const {
    data: orgUnitSharedData,
    error: orgUnitSharedDataError,
    loading: orgUnitDataLoading
  } = useQuery(GET_ORG_UNIT_SHARED_DATA, {
    variables: { orgUnitId: props.orgUnitId }
  });

  const [setOrgUnitShared, { loading: setOrgUnitSharedLoading, error: setOrgUnitSharedError }] =
    useMutation(SET_ORG_UNIT_SHARED_DATA, {
      refetchQueries: [
        {
          query: GET_ORG_UNIT_SHARED_DATA,
          variables: { orgUnitId: props.orgUnitId }
        }
      ]
    });

  const programYearOptions = useMemo(
    () =>
      appraisalData
        ? appraisalData.mddapOrgUnitProgramYears
            .map(oua => ({
              label: `Y${oua.AppraisalProgramYear}`,
              value: oua.AppraisalId
            }))
            .reverse()
        : [],
    [appraisalData]
  );

  const performanceItemOptions = useMemo(
    () =>
      performanceData
        ? performanceData.mddapOrgUnitPerformanceReport.map(performanceItem => ({
            label: performanceItem.MeasurementName,
            value: performanceItem.Id
          }))
        : [],
    [performanceData]
  );

  useEffect(() => {
    if (!appraisalData || !orgUnitSharedData) {
      return;
    }

    const selectedSharedDetailedAppraisalReports = getSelectedOptionsData(
      programYearOptions,
      orgUnitSharedData.mddapOrgUnitFdaSharing
        ? orgUnitSharedData.mddapOrgUnitFdaSharing.SharedDetailedAppraisalReports
        : [],
      year => year.AppraisalId
    );

    const selectedSharedPracticeCharacterizationReports = getSelectedOptionsData(
      programYearOptions,
      orgUnitSharedData.mddapOrgUnitFdaSharing
        ? orgUnitSharedData.mddapOrgUnitFdaSharing.SharedPracticeCharacterizationReports
        : [],
      year => year.AppraisalId
    );

    setSelectedAppraisalProgramYears(selectedSharedDetailedAppraisalReports);

    setSelectedPracticeProgramYears(selectedSharedPracticeCharacterizationReports);
  }, [appraisalData, orgUnitSharedData, programYearOptions]);

  useEffect(() => {
    if (!performanceData || !orgUnitSharedData) {
      return;
    }

    const selectedSharedPerformanceReports = getSelectedOptionsData(
      performanceItemOptions,
      orgUnitSharedData.mddapOrgUnitFdaSharing
        ? orgUnitSharedData.mddapOrgUnitFdaSharing.SharedPerformanceReports
        : [],
      scp => scp.MddapCheckpointPerformanceItemId
    );

    setSelectedPerformanceItems(selectedSharedPerformanceReports);
  }, [performanceData, orgUnitSharedData, performanceItemOptions]);

  const getSelectedOptionsData = (availableOptions, data, dataOptionMatcher) => {
    const isShareFuture = data.length === 1 && dataOptionMatcher(data[0]) === null;

    const options = isShareFuture
      ? availableOptions
      : data
          .filter(year => availableOptions.some(x => x.value == dataOptionMatcher(year)))
          .map(year => {
            const cp = availableOptions.find(x => x.value == dataOptionMatcher(year));
            return {
              label: cp.label,
              value: cp.value
            };
          });

    return {
      isShareFuture: isShareFuture,
      options: _.sortBy(options, item => item.label)
    };
  };

  const storedAppraisalProgramYears = getSelectedOptionsData(
    programYearOptions,
    orgUnitSharedData && orgUnitSharedData.mddapOrgUnitFdaSharing
      ? orgUnitSharedData.mddapOrgUnitFdaSharing.SharedDetailedAppraisalReports
      : [],
    year => year.AppraisalId
  );

  const storedPracticeProgramYears = getSelectedOptionsData(
    programYearOptions,
    orgUnitSharedData && orgUnitSharedData.mddapOrgUnitFdaSharing
      ? orgUnitSharedData.mddapOrgUnitFdaSharing.SharedPracticeCharacterizationReports
      : [],
    year => year.AppraisalId
  );

  const storedPerformanceItems = getSelectedOptionsData(
    performanceItemOptions,
    orgUnitSharedData && orgUnitSharedData.mddapOrgUnitFdaSharing
      ? orgUnitSharedData.mddapOrgUnitFdaSharing.SharedPerformanceReports
      : [],
    scp => scp.MddapCheckpointPerformanceItemId
  );

  const isSubmitDisabled = useMemo(() => {
    return (
      storedPerformanceItems.isShareFuture === selectedPerformanceItems.isShareFuture &&
      storedAppraisalProgramYears.isShareFuture === selectedAppraisalProgramYears.isShareFuture &&
      storedPracticeProgramYears.isShareFuture === selectedPracticeProgramYears.isShareFuture &&
      _.isEqual(storedPerformanceItems.options, selectedPerformanceItems.options) &&
      _.isEqual(storedAppraisalProgramYears.options, selectedAppraisalProgramYears.options) &&
      _.isEqual(storedPracticeProgramYears.options, selectedPracticeProgramYears.options)
    );
  }, [
    storedPerformanceItems,
    storedAppraisalProgramYears,
    storedPracticeProgramYears,
    selectedPerformanceItems,
    selectedAppraisalProgramYears,
    selectedPracticeProgramYears
  ]);

  const submitSharedData = async () => {
    const sharedOrgUnitData = orgUnitSharedData.mddapOrgUnitFdaSharing;
    let removedPerformanceItemIds = [];
    let addedPerformanceItemIds = selectedPerformanceItems.options;
    let removedAppraisalIds = [];
    let addedAppraisalIds = selectedAppraisalProgramYears.options;
    let removedPracticeCharacterizationIds = [];
    let addedPracticeCharacterizationIds = selectedPracticeProgramYears.options;

    if (sharedOrgUnitData) {
      const sharedPerformanceData = sharedOrgUnitData.SharedPerformanceReports;
      const sharedAppraisalData = sharedOrgUnitData.SharedDetailedAppraisalReports;
      const sharedPracticeCharacterizationData =
        sharedOrgUnitData.SharedPracticeCharacterizationReports;

      removedPerformanceItemIds = selectedPerformanceItems.isShareFuture
        ? sharedPerformanceData
        : sharedPerformanceData.filter(
            x =>
              !selectedPerformanceItems.options
                .map(y => y.value)
                .includes(x.MddapCheckpointPerformanceItemId)
          );
      addedPerformanceItemIds = selectedPerformanceItems.options.filter(
        x => !sharedPerformanceData.map(y => y.MddapCheckpointPerformanceItemId).includes(x.value)
      );

      removedAppraisalIds = selectedAppraisalProgramYears.isShareFuture
        ? sharedAppraisalData
        : sharedAppraisalData.filter(
            x => !selectedAppraisalProgramYears.options.map(y => y.value).includes(x.AppraisalId)
          );
      addedAppraisalIds = selectedAppraisalProgramYears.options.filter(
        x => !sharedAppraisalData.map(y => y.AppraisalId).includes(x.value)
      );

      removedPracticeCharacterizationIds = selectedPracticeProgramYears.isShareFuture
        ? sharedPracticeCharacterizationData
        : sharedPracticeCharacterizationData.filter(
            x => !selectedPracticeProgramYears.options.map(y => y.value).includes(x.AppraisalId)
          );
      addedPracticeCharacterizationIds = selectedPracticeProgramYears.options.filter(
        x => !sharedPracticeCharacterizationData.map(y => y.AppraisalId).includes(x.value)
      );
    }

    const shareOrgUnitData = {
      MddapOrgUnitId: props.orgUnitId,
      SharedPerformanceReports: selectedPerformanceItems.isShareFuture
        ? [{ MddapCheckpointPerformanceItemId: null }]
        : addedPerformanceItemIds.map(x => ({ MddapCheckpointPerformanceItemId: x.value })),
      SharedDetailedAppraisalReports: selectedAppraisalProgramYears.isShareFuture
        ? [{ AppraisalId: null }]
        : addedAppraisalIds.map(x => ({ AppraisalId: x.value })),
      SharedPracticeCharacterizationReports: selectedPracticeProgramYears.isShareFuture
        ? [{ AppraisalId: null }]
        : addedPracticeCharacterizationIds.map(x => ({ AppraisalId: x.value })),

      DeleteSharedPerformanceReports: removedPerformanceItemIds.map(x => ({
        MddapCheckpointPerformanceItemId: x.MddapCheckpointPerformanceItemId
      })),
      DeleteSharedDetailedAppraisalReports: removedAppraisalIds.map(x => ({
        AppraisalId: x.AppraisalId
      })),
      DeleteSharedPracticeCharacterizationReports: removedPracticeCharacterizationIds.map(x => ({
        AppraisalId: x.AppraisalId
      }))
    };

    await setOrgUnitShared({ variables: { data: shareOrgUnitData } });

    setResultMessage(getResultSuccessMessage());
    setIsEdit(false);
  };

  const getResultSuccessMessage = () => {
    const sharedAppraisalYears = selectedAppraisalProgramYears.options.filter(x => x.value != 0);
    const appraisalYearsMessage = sharedAppraisalYears.length
      ? `Detailed Appraisal Report (${joinFormatted(sharedAppraisalYears.map(x => x.label))}) ` +
        `${getHasHavePlural(sharedAppraisalYears.length)} been shared with FDA`
      : '';
    const sharedPerformanceItems = selectedPerformanceItems.options.filter(x => x.value != 0);
    const performanceItemsMessage = sharedPerformanceItems.length
      ? `Actual Performance Report (${joinFormatted(sharedPerformanceItems.map(x => x.label))}) ` +
        `${getHasHavePlural(sharedPerformanceItems.length)} been shared with FDA`
      : '';
    const sharedPracticeCharacterizationYears = selectedPracticeProgramYears.options.filter(
      x => x.value != 0
    );
    const practiceCharacterizationYearsMessage = sharedPracticeCharacterizationYears.length
      ? `Practice Characterization Graph (${joinFormatted(
          sharedPracticeCharacterizationYears.map(x => x.label)
        )}) ` +
        `${getHasHavePlural(sharedPracticeCharacterizationYears.length)} been shared with FDA.`
      : '';

    const resultMessage = buildResultMessage([
      appraisalYearsMessage,
      performanceItemsMessage,
      practiceCharacterizationYearsMessage
    ]);
    return (
      <div id='resultMessage'>
        <SuccessMessage message={resultMessage} />
      </div>
    );
  };

  const buildResultMessage = messages => {
    let messageList = [];
    messages.forEach(message => {
      if (message !== '') {
        messageList.push(message);
      }
    });
    if (messageList.length === 0) {
      return ''; // no messages
    }
    return joinFormatted(messageList.map(x => x));
  };

  const handleChangeOptionsProvider = (valueSetter, totalOptionLength) => items => {
    const options = _.sortBy(items, item => item.label);
    const isShareFuture = options.length === totalOptionLength;
    valueSetter(prev => ({ ...prev, options, isShareFuture }));
  };

  const handleChangeShareFutureProvider = valueSetter => isShareFuture => {
    valueSetter(prev => ({ ...prev, isShareFuture }));
  };

  const handleCancelClick = () => {
    setIsEdit(false);
    setSelectedPerformanceItems(storedPerformanceItems);
    setSelectedAppraisalProgramYears(storedAppraisalProgramYears);
    setSelectedPracticeProgramYears(storedPracticeProgramYears);
    setResultMessage('');
  };

  const handleEditClick = () => {
    setIsEdit(true);
    setResultMessage('');
  };

  const getSelectionListHeader = (headerText, subHeaderText) => {
    return (
      <div className='head-label-content'>
        <span className='filter-text-head'>{headerText}</span>
        <span>{subHeaderText}</span>
      </div>
    );
  };

  if (
    setOrgUnitSharedError ||
    appraisalDataError ||
    performanceDataError ||
    orgUnitSharedDataError
  ) {
    return <ApolloError />;
  }

  return (
    <div className='report-management-customization'>
      <div className='report-management-description'>
        <TitleLabel data-testid='title-label'>FDA Report Management</TitleLabel>
        <div className='report-management-description__text'>
          <span className='report-management-description__text-summary'>
            This page allows Organizations to provide additional transparency with the FDA. Select
            any of the following individual reports to share with FDA. Select All will share all
            existing report data, with the option to share all future report data via the check box
            below the dropdown. The X to the right of selected reports will clear all selections.
          </span>
          <div>
            <ActionButtonsGroup
              isEdit={isEdit}
              isSubmitting={!!setOrgUnitSharedLoading}
              handleCancelClick={handleCancelClick}
              handleEditClick={handleEditClick}
              handleSubmitClick={submitSharedData}
              isSubmitDisabled={isSubmitDisabled}></ActionButtonsGroup>
            {isEdit &&
              (selectedPerformanceItems.isShareFuture ||
                selectedAppraisalProgramYears.isShareFuture ||
                selectedPracticeProgramYears.isShareFuture) && (
                <div className='future-warning-message'>
                  This selection will share future data with the FDA
                </div>
              )}
          </div>
        </div>
      </div>
      <div className='report-management-customization__wrapper'>
        <div className='report-management-customization__sections'>
          <div className='report-management-customization__section'>
            {getSelectionListHeader('Organization Report', 'Detailed Appraisal Report')}
            <SelectionList
              id='select-appraisal-program-year'
              showInputEndAdornment={false}
              allowAutocomplete={false}
              onSelectHandler={handleChangeOptionsProvider(
                setSelectedAppraisalProgramYears,
                programYearOptions.length
              )}
              selectedValue={selectedAppraisalProgramYears.options}
              multiSelect
              disabled={!isEdit}
              list={programYearOptions}
              addSelectAll
              placeholder='Select a Program Year'
              loading={appraisalDataLoading || orgUnitDataLoading}
            />
            <ShareFutureDataCheckBox
              shareFutureData={selectedAppraisalProgramYears.isShareFuture}
              setShareFutureData={handleChangeShareFutureProvider(setSelectedAppraisalProgramYears)}
              isSelectAll={
                programYearOptions.length === selectedAppraisalProgramYears.options.length
              }
              isEdit={isEdit}
              loading={appraisalDataLoading || orgUnitDataLoading}
            />
          </div>
          <div className='report-management-customization__section'>
            {getSelectionListHeader('Organization Report', 'Actual Performance Report')}
            <SelectionList
              id='select-performance-date'
              showInputEndAdornment={false}
              allowAutocomplete={false}
              onSelectHandler={handleChangeOptionsProvider(
                setSelectedPerformanceItems,
                performanceItemOptions.length
              )}
              selectedValue={selectedPerformanceItems.options}
              list={performanceItemOptions}
              multiSelect
              disabled={!isEdit}
              addSelectAll
              placeholder='Select a Metric'
              loading={performanceDataLoading || orgUnitDataLoading}
            />
            <ShareFutureDataCheckBox
              shareFutureData={selectedPerformanceItems.isShareFuture}
              setShareFutureData={handleChangeShareFutureProvider(setSelectedPerformanceItems)}
              isSelectAll={
                performanceItemOptions.length === selectedPerformanceItems.options.length
              }
              isEdit={isEdit}
              loading={performanceDataLoading || orgUnitDataLoading}
            />
          </div>
          <div className='report-management-customization__section'>
            {getSelectionListHeader('Appraisal Comparison', 'Practice Characterization Graph')}
            <SelectionList
              id='select-practice-characterization-program-year'
              showInputEndAdornment={false}
              allowAutocomplete={false}
              onSelectHandler={handleChangeOptionsProvider(
                setSelectedPracticeProgramYears,
                programYearOptions.length
              )}
              selectedValue={selectedPracticeProgramYears.options}
              multiSelect
              disabled={!isEdit}
              list={programYearOptions}
              addSelectAll
              placeholder='Select a Program Year'
              loading={appraisalDataLoading || orgUnitDataLoading}
            />
            <ShareFutureDataCheckBox
              shareFutureData={selectedPracticeProgramYears.isShareFuture}
              setShareFutureData={handleChangeShareFutureProvider(setSelectedPracticeProgramYears)}
              isSelectAll={
                programYearOptions.length === selectedPracticeProgramYears.options.length
              }
              isEdit={isEdit}
              loading={appraisalDataLoading || orgUnitDataLoading}
            />
          </div>
        </div>
        <div className='report-management-customization__sections-result'>{resultMessage}</div>
      </div>
    </div>
  );
};

export default FdaReportManagement;
